import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { 
  Users, TrendingUp, Euro, Search, RefreshCw, 
  CheckCircle, XCircle, Settings, Network
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { toast } from 'sonner'

interface AdminStats {
  utenti_totali: number
  utenti_attivi: number
  apc_mese_corrente: number
  volume_mese_corrente: number
  commissioni_da_pagare: number
}

interface AdminUser {
  id: number
  email: string
  nome: string
  tipo: string | null
  livello: string | null
  attivo: boolean
  zero_canone: boolean
  licenza_pagata: boolean
  data_attivazione: string | null
  apc_diretti: number
  vq_rete: number
}

interface Commissione {
  id: number
  consulente_id: number
  consulente_nome: string
  consulente_email: string
  anno_mese: string
  commissioni_dirette: number
  commissioni_rete: number
  totale: number
  stato: string
  data_pagamento: string | null
}

export function IxenAdmin() {
  const { token } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [commissioni, setCommissioni] = useState<Commissione[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterTipo] = useState<string>('')
  const [filterLivello] = useState<string>('')
  
  useEffect(() => {
    fetchDashboard()
    fetchUsers()
    fetchCommissioni()
  }, [])
  
  const fetchDashboard = async () => {
    try {
      const res = await fetch('/api/v1/admin/ixen/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setStats(data.statistiche)
      }
    } catch (error) {
      console.error('Error fetching admin dashboard:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterTipo) params.append('tipo', filterTipo)
      if (filterLivello) params.append('livello', filterLivello)
      
      const res = await fetch(`/api/v1/admin/ixen/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setUsers(data.users || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }
  
  const fetchCommissioni = async () => {
    try {
      const res = await fetch('/api/v1/admin/ixen/commissioni?stato=calcolato', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setCommissioni(data.commissioni || [])
      }
    } catch (error) {
      console.error('Error fetching commissioni:', error)
    }
  }
  
  const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const endpoint = currentStatus ? 'disattiva' : 'attiva'
      const res = await fetch(`/api/v1/admin/ixen/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        toast.success(`Utente ${currentStatus ? 'disattivato' : 'attivato'}`)
        fetchUsers()
        fetchDashboard()
      }
    } catch (error) {
      toast.error('Errore durante l\'operazione')
    }
  }
  
  const approvaCommissione = async (commissioneId: number) => {
    try {
      const res = await fetch(`/api/v1/admin/ixen/commissioni/${commissioneId}/approva`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        toast.success('Commissione approvata')
        fetchCommissioni()
      }
    } catch (error) {
      toast.error('Errore durante l\'approvazione')
    }
  }
  
  const initLivelli = async () => {
    try {
      const res = await fetch('/api/v1/admin/ixen/init-livelli', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        toast.success('Livelli iXen inizializzati')
      }
    } catch (error) {
      toast.error('Errore durante l\'inizializzazione')
    }
  }
  
  const calcolaCommissioniMese = async () => {
    const mese = new Date().toISOString().slice(0, 7)
    try {
      const res = await fetch(`/api/v1/admin/ixen/calcola-commissioni-mese?mese=${mese}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        toast.success(`Commissioni calcolate per ${data.processati} consulenti`)
        fetchCommissioni()
      }
    } catch (error) {
      toast.error('Errore durante il calcolo')
    }
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gestione iXen</h1>
          <p className="text-slate-400">Amministrazione network marketing</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={initLivelli}
            className="border-slate-600"
          >
            <Settings className="w-4 h-4 mr-2" />
            Init Livelli
          </Button>
          <Button 
            onClick={calcolaCommissioniMese}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Calcola Commissioni
          </Button>
        </div>
      </div>
      
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Utenti Totali</p>
                  <p className="text-2xl font-bold">{stats.utenti_totali}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Utenti Attivi</p>
                  <p className="text-2xl font-bold">{stats.utenti_attivi}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">APC Mese</p>
                  <p className="text-2xl font-bold">{stats.apc_mese_corrente}</p>
                </div>
                <Network className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Volume Mese</p>
                  <p className="text-2xl font-bold">{stats.volume_mese_corrente.toFixed(0)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Comm. da Pagare</p>
                  <p className="text-2xl font-bold text-green-500">
                    {stats.commissioni_da_pagare.toFixed(2)}
                  </p>
                </div>
                <Euro className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="users">Utenti iXen</TabsTrigger>
          <TabsTrigger value="commissioni">Commissioni</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca per email o nome..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                    className="pl-10 bg-slate-700 border-slate-600"
                  />
                </div>
                <Button onClick={fetchUsers} variant="outline" className="border-slate-600">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead>Utente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Livello</TableHead>
                    <TableHead>APC Dir.</TableHead>
                    <TableHead>VQ Rete</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="border-slate-700">
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.nome || '-'}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-600">
                          {user.tipo || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-600">
                          {user.livello || 'L1'}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.apc_diretti}</TableCell>
                      <TableCell>{user.vq_rete}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {user.attivo ? (
                            <Badge className="bg-green-600">Attivo</Badge>
                          ) : (
                            <Badge className="bg-slate-600">Inattivo</Badge>
                          )}
                          {user.zero_canone && (
                            <Badge className="bg-amber-600">Zero Canone</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant={user.attivo ? 'destructive' : 'default'}
                          onClick={() => toggleUserStatus(user.id, user.attivo)}
                        >
                          {user.attivo ? (
                            <><XCircle className="w-4 h-4 mr-1" /> Disattiva</>
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-1" /> Attiva</>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {users.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  Nessun utente iXen trovato
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="commissioni" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle>Commissioni da Approvare</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead>Consulente</TableHead>
                    <TableHead>Mese</TableHead>
                    <TableHead>Dirette</TableHead>
                    <TableHead>Rete</TableHead>
                    <TableHead>Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissioni.map((comm) => (
                    <TableRow key={comm.id} className="border-slate-700">
                      <TableCell>
                        <div>
                          <p className="font-medium">{comm.consulente_nome || '-'}</p>
                          <p className="text-sm text-slate-400">{comm.consulente_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{comm.anno_mese}</TableCell>
                      <TableCell>{comm.commissioni_dirette.toFixed(2)}</TableCell>
                      <TableCell>{comm.commissioni_rete.toFixed(2)}</TableCell>
                      <TableCell className="font-bold text-green-500">
                        {comm.totale.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          comm.stato === 'pagato' ? 'bg-green-600' :
                          comm.stato === 'approvato' ? 'bg-blue-600' :
                          'bg-amber-600'
                        }>
                          {comm.stato}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {comm.stato === 'calcolato' && (
                          <Button
                            size="sm"
                            onClick={() => approvaCommissione(comm.id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approva
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {commissioni.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  Nessuna commissione da approvare
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
